<script>
export default {
    props: ['historyItem'],
    data() {
        return {
            showModal: false,
            fields: [
                { key: "platform", label: "Platform" },
                { key: "status", label: "Status" },
                { key: "link", label: "Post" },
                { key: "error", label: "Error" },
            ],
        };
    },
    computed: {
        // Summary data comes from the top-level post fields.
        post() {
            return this.historyItem || {};
        },
        // meta_data is only used for per-platform results (links / errors).
        meta() {
            return this.post.meta_data ? this.post.meta_data : {};
        },
        overallStatus() {
            return this.post.status || "";
        },
        // Summary status pill. Like the history table, a post that published to
        // some platforms but failed on others is shown as "Partial".
        overallStatusMeta() {
            const results = this.platformResults;
            const succeeded = results.filter(r => r.status === "completed").length;
            const failed = results.filter(r => r.status === "failed").length;
            if (succeeded > 0 && failed > 0) {
                return { label: "Partial", variant: "partial" };
            }
            return this.statusMeta(this.overallStatus);
        },
        // Per-platform results, normalized from both meta_data shapes:
        // an array of objects (new) or a platform-keyed object (old).
        platformResults() {
            const results = this.meta.results;
            if (Array.isArray(results)) {
                return results.map(r => ({
                    platform: r.platform,
                    url: r.post_url,
                    success: r.success,
                    status: r.status ? r.status : r.success ? "completed" : "failed",
                    error: r.error_message,
                    post_id: r.platform_post_id,
                    media_type: r.media_type,
                    media_size_bytes: r.media_size_bytes,
                }));
            }
            if (results && typeof results === "object") {
                return Object.keys(results).map(platform => ({
                    platform,
                    url: results[platform].url,
                    success: results[platform].success,
                    status: results[platform].status,
                    error: results[platform].error,
                    post_id: results[platform].post_id,
                    media_type: results[platform].media_type,
                    media_size_bytes: null,
                }));
            }
            return [];
        },
        // Per-platform engagement metrics from post_metrics. Each entry is
        // either { metrics: {...} } or { error: "..." } when the fetch failed.
        postMetrics() {
            const pm = this.post.post_metrics;
            if (!pm || typeof pm !== "object") return [];
            return Object.keys(pm).map(platform => ({
                platform,
                metrics: pm[platform] ? pm[platform].metrics : null,
                error: pm[platform] ? pm[platform].error : null,
            }));
        },
        totalEngagement() {
            const total = this.post.post_metrics_total;
            return (total && Number(total.total_engagement)) || 0;
        },
    },
    methods: {
        closeModal() {
            this.showModal = false;
        },
        statusMeta(status) {
            if (status === "completed") return { label: "Published", variant: "success" };
            if (status === "queued") return { label: "Scheduled", variant: "info" };
            if (status === "processing" || status === "pending" || status === "in_progress") return { label: "Processing", variant: "warning" };
            if (status === "failed") return { label: "Failed", variant: "danger" };
            if (!status) return { label: "—", variant: "secondary" };
            return { label: status.charAt(0).toUpperCase() + status.slice(1), variant: "secondary" };
        },
        platformMeta(key) {
            const map = {
                instagram: { color: "#E1306C", icon: "mdi mdi-instagram" },
                tiktok: { color: "#111111", icon: "mdi mdi-music-note" },
                x: { color: "#1DA1F2", icon: "mdi mdi-twitter" },
                twitter: { color: "#1DA1F2", icon: "mdi mdi-twitter" },
                facebook: { color: "#1877F2", icon: "mdi mdi-facebook" },
                youtube: { color: "#FF0000", icon: "mdi mdi-youtube" },
                linkedin: { color: "#0A66C2", icon: "mdi mdi-linkedin" },
            };
            return map[key] || { color: "#6c757d", icon: "mdi mdi-share-variant" };
        },
        // Turn a platform's metrics object into labelled { label, value } pairs.
        metricEntries(metrics) {
            if (!metrics || typeof metrics !== "object") return [];
            const labels = {
                likes: "Likes", comments: "Comments", shares: "Shares",
                saves: "Saves", views: "Views", impressions: "Impressions", reach: "Reach",
            };
            return Object.keys(metrics).map(key => ({
                label: labels[key] || (key.charAt(0).toUpperCase() + key.slice(1)),
                value: metrics[key],
            }));
        },
    },
};
</script>

<template>
    <b-modal id="view_post_details" size="lg" v-model="showModal" title="Post Details" title-class="font-18">
        <template v-slot:modal-header>
            <div class="w-100">
                <h5 class="modal-title mb-0">Post Details</h5>
                <p class="text-muted mb-0 mt-2">What was submitted and where it was published</p>
            </div>
            <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
        </template>

        <!-- Submission summary -->
        <div class="card mb-3">
            <div class="card-body py-2">
                <h4 class="card-title mb-3">Submission</h4>
                <div class="row">
                    <div class="col-md-6 mb-2">
                        <span class="text-muted d-block">Type</span>
                        <span class="badge bg-light text-dark">{{ post.action_type }}</span>
                    </div>
                    <div class="col-md-6 mb-2">
                        <span class="text-muted d-block">Status</span>
                        <span class="status-pill" :class="`status-pill--${overallStatusMeta.variant}`">
                            <span class="status-dot"></span>{{ overallStatusMeta.label }}
                        </span>
                    </div>
                    <div class="col-md-6 mb-2">
                        <span class="text-muted d-block">Submitted</span>
                        <span>{{ post.timestamp }}</span>
                    </div>
                    <div v-if="post.scheduled_date" class="col-md-6 mb-2">
                        <span class="text-muted d-block">Scheduled at</span>
                        <span>{{ post.scheduled_date }}</span>
                    </div>
                    <div v-if="post.content" class="col-md-6 mb-2">
                        <span class="text-muted d-block">Content</span>
                        <span>{{ post.content }}</span>
                    </div>
                    <div v-if="post.description" class="col-md-6 mb-2">
                        <span class="text-muted d-block">Description</span>
                        <span>{{ post.description }}</span>
                    </div>
                    <div v-if="post.link_url" class="col-md-6 mb-2">
                        <span class="text-muted d-block">Link</span>
                        <a :href="post.link_url" target="_blank" rel="noopener" class="text-primary">{{ post.link_url }}</a>
                    </div>
                    <div class="col-md-6 mb-2">
                        <span class="text-muted d-block">Request ID</span>
                        <span class="font-size-12">{{ post.request_id }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Per-platform results -->
        <div class="card mb-3">
            <div class="card-body py-2">
                <h4 class="card-title mb-3">Platforms</h4>
                <div class="table-responsive mb-0 font-size-13">
                    <b-table
                        :items="platformResults"
                        :fields="fields"
                        responsive="sm"
                        show-empty
                        empty-text="No platform results"
                        table-class="table table-centered mb-0"
                    >
                        <template v-slot:cell(platform)="data">
                            <span class="platform-dot me-2" :style="{ backgroundColor: platformMeta(data.item.platform).color }"><i :class="platformMeta(data.item.platform).icon"></i></span>
                            <span class="text-capitalize">{{ data.item.platform }}</span>
                        </template>

                        <template v-slot:cell(status)="data">
                            <span class="status-pill" :class="`status-pill--${statusMeta(data.item.status).variant}`">
                                <span class="status-dot"></span>{{ statusMeta(data.item.status).label }}
                            </span>
                        </template>

                        <template v-slot:cell(link)="data">
                            <a v-if="data.item.url" :href="data.item.url" target="_blank" rel="noopener" class="text-primary">
                                <i class="mdi mdi-open-in-new me-1"></i>Open
                            </a>
                            <span v-else class="text-muted">—</span>
                        </template>

                        <template v-slot:cell(error)="data">
                            <span v-if="data.item.error" class="text-danger">{{ data.item.error }}</span>
                            <span v-else class="text-muted">—</span>
                        </template>
                    </b-table>
                </div>
            </div>
        </div>

        <!-- Per-platform engagement metrics -->
        <div class="card mb-0">
            <div class="card-body py-2">
                <div class="d-flex align-items-center justify-content-between mb-3">
                    <h4 class="card-title mb-0">Engagement</h4>
                    <span class="badge bg-light text-dark">Total: {{ totalEngagement }}</span>
                </div>
                <div v-if="postMetrics.length" class="d-flex flex-column gap-3">
                    <div v-for="m in postMetrics" :key="m.platform" class="d-flex align-items-start">
                        <span class="platform-dot me-2" :style="{ backgroundColor: platformMeta(m.platform).color }"><i :class="platformMeta(m.platform).icon"></i></span>
                        <div class="min-w-0">
                            <div class="text-capitalize fw-semibold">{{ m.platform }}</div>
                            <div v-if="m.error" class="text-danger font-size-13">{{ m.error }}</div>
                            <div v-else-if="metricEntries(m.metrics).length" class="d-flex flex-wrap gap-2 mt-1">
                                <span v-for="e in metricEntries(m.metrics)" :key="e.label" class="badge bg-light text-dark">
                                    {{ e.label }}: {{ e.value }}
                                </span>
                            </div>
                            <div v-else class="text-muted font-size-13">No metrics available</div>
                        </div>
                    </div>
                </div>
                <p v-else class="text-muted mb-0">No metrics available</p>
            </div>
        </div>

        <template v-slot:modal-footer>
            <b-button variant="light" @click="closeModal">Close</b-button>
        </template>
    </b-modal>
</template>

<style scoped>
    .platform-dot {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        color: #fff;
        font-size: 15px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
    }
    .status-pill {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-size: 0.8125rem;
        font-weight: 500;
        padding: 0.25rem 0.6rem;
        border-radius: 999px;
    }
    .status-pill .status-dot {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: currentColor;
    }
    .status-pill--success { color: #34c38f; background: rgba(52, 195, 143, 0.12); }
    .status-pill--info { color: #50a5f1; background: rgba(80, 165, 241, 0.12); }
    .status-pill--warning { color: #f1b44c; background: rgba(241, 180, 76, 0.12); }
    .status-pill--danger { color: #f46a6a; background: rgba(244, 106, 106, 0.12); }
    .status-pill--partial { color: #fd7e14; background: rgba(253, 126, 20, 0.12); }
    .status-pill--secondary { color: #74788d; background: rgba(116, 120, 141, 0.12); }
    .min-w-0 {
        min-width: 0;
    }
</style>
